import * as Yup from 'yup';
import moment from 'moment';
import { WorkerProfileField } from 'types';
import { IFile } from 'models/general';
import { GOV_ID_TYPE_OTHER_OPTION_ID } from 'constants/common';
import { shouldEnforceFieldAsMandatory } from 'utils/skippableFieldsUtils';
import { EMAIL_REGEX, isValidMobileNumber, extractNationalNumber, minLengthIfFilled } from 'utils';

export interface IProfileInformationSchema {
  // Personal Information
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string | null;
  gender: number | null;
  ethnicityId: string | null;
  primaryLanguageId: string | null;
  isVeteran: boolean | null;
  allergies: string | null;
  socialSecurityNumber: string | null;
  pictureUrl: string | null;

  // Government ID
  identificationTypeId: string | null;
  identificationGeographicLocationId: string | null;
  identificationNumber: string | null;
  otherIdentificationType: string | null;
  governmentDocumentFiles: Array<IFile>;

  // Contact Information
  email: string;
  mobileNumber: string;
  phoneNumber: string;

  // Home Address
  addressLine1: string;
  addressLine2: string;
  country: string;
  countryName: string;
  state: string;
  stateName: string;
  city: string;
  zipCode: string;
  county: string;
  borough: string;
  latitude: number | undefined;
  longitude: number | undefined;

  // Emergency Contact
  emergencyContactName: string;
  emergencyContactNumber: string;
  emergencyContactRelationship: string;

  // Worker Trades
  isSupervisor: boolean | null;
  isAffiliatedToLaborUnion: boolean | null;
  isSkilled: boolean | null;
  laborUnionNumber: string;
  trades: string[];
  otherTradesList: Array<{ isSaved: boolean; OtherText: string }>;
}

const profileInformationSchema = (t: any, profileFieldConstraintsMap: any) => {
  const isFieldRequired = (field: WorkerProfileField): boolean =>
    shouldEnforceFieldAsMandatory(field, profileFieldConstraintsMap, true) ||
    !!profileFieldConstraintsMap[field]?.isSkippable;

  return Yup.object().shape({
    // ── Personal Information ─────────────────────────────────────────────
    firstName: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.workerFirstName),
      then: schema => schema.required(t('personalInformation.errors.firstNameRequired')).trim(),
    }),

    middleName: Yup.string()
      .nullable()
      .notRequired()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerMiddleName),
        then: schema => schema.required(t('personalInformation.errors.middleNameRequired')).trim(),
      }),

    lastName: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.workerLastName),
      then: schema => schema.required(t('personalInformation.errors.lastNameRequired')).trim(),
    }),

    dateOfBirth: Yup.mixed()
      .nullable()
      .test('is-valid-date', t('basicInformation.errors.invalidDate'), value => {
        if (!value) return true;
        if (moment.isMoment(value)) return value.isValid();
        if (typeof value === 'string' || value instanceof Date) return moment(value).isValid();
        return false;
      })
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerDateOfBirth),
        then: schema => schema.required(t('personalInformation.errors.dateOfBirthRequired')),
      }),

    gender: Yup.number()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerGender),
        then: schema => schema.required(t('personalInformation.errors.genderRequired')),
      }),

    ethnicityId: Yup.string()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerEthnicity),
        then: schema => schema.required(t('personalInformation.errors.ethnicityRequired')),
      }),

    primaryLanguageId: Yup.string()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerPrimaryLanguage),
        then: schema => schema.required(t('personalInformation.errors.primaryLanguageRequired')),
      }),

    isVeteran: Yup.boolean()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerVeteranStatus),
        then: schema => schema.required(t('personalInformation.errors.veteranStatusRequired')),
      }),

    allergies: Yup.string()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerAllergies),
        then: schema => schema.required(t('personalInformation.errors.allergiesRequired')),
      }),

    socialSecurityNumber: Yup.string()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerLastSSN),
        then: schema =>
          schema
            .required(t('personalInformation.errors.lastSSNRequired'))
            .matches(/^\d{4}$/, t('personalInformation.errors.invalidSSN')),
      }),

    pictureUrl: Yup.string()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerProfilePhoto),
        then: schema => schema.required(t('personalInformation.errors.profilePictureRequired')),
      }),

    // ── Government ID ────────────────────────────────────────────────────
    identificationTypeId: Yup.string()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerGovtIdType),
        then: schema => schema.required(t('personalInformation.errors.idTypeRequired')),
      }),

    identificationGeographicLocationId: Yup.string()
      .nullable()
      .when(['identificationTypeId'], {
        is: (val: string) =>
          val !== GOV_ID_TYPE_OTHER_OPTION_ID && isFieldRequired(WorkerProfileField.workerGovtIdIssuedBy),
        then: schema => schema.required(t('personalInformation.errors.issuedByRequired')),
      }),

    otherIdentificationType: Yup.string()
      .nullable()
      .when(['identificationTypeId'], {
        is: (val: string) => val === GOV_ID_TYPE_OTHER_OPTION_ID,
        then: schema => schema.required(t('personalInformation.errors.issuedByRequired')),
      }),

    identificationNumber: Yup.string()
      .nullable()
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.workerGovtIdNumber),
        then: schema =>
          schema
            .required(t('personalInformation.errors.idNumberRequired'))
            .trim()
            .test(minLengthIfFilled(4, t('personalInformation.errors.idNumberMin')))
            .max(20, t('personalInformation.errors.idNumberMax')),
      }),

    governmentDocumentFiles: Yup.array().when([], {
      is: () => isFieldRequired(WorkerProfileField.workerGovtIdImage),
      then: schema =>
        schema
          .min(1, t('personalInformation.errors.idImageRequired'))
          .required(t('personalInformation.errors.idImageRequired')),
    }),

    // ── Contact Information ──────────────────────────────────────────────
    email: Yup.string()
      .matches(EMAIL_REGEX, { message: t('contactInformation.errors.emailInvalid'), excludeEmptyString: true })
      .when([], {
        is: () => isFieldRequired(WorkerProfileField.email),
        then: schema => schema.required(t('contactInformation.errors.emailRequired')),
      }),

    mobileNumber: Yup.string().test('mobile-validation', t('contactInformation.errors.phoneInvalid'), function (value) {
      const isMandatory = isFieldRequired(WorkerProfileField.mobilePhoneNumber);
      const nationalNumber = extractNationalNumber(value);
      const hasNumber = nationalNumber && nationalNumber.trim() !== '';
      if (isMandatory && !hasNumber) {
        return this.createError({ message: t('contactInformation.errors.phoneRequired') });
      }
      if (hasNumber) {
        return isValidMobileNumber(value);
      }
      return true;
    }),

    phoneNumber: Yup.string().test(
      'alt-phone-validation',
      t('contactInformation.errors.phoneInvalid'),
      function (value) {
        const nationalNumber = extractNationalNumber(value);
        const hasNumber = nationalNumber && nationalNumber.trim() !== '';
        if (!hasNumber && !isFieldRequired(WorkerProfileField.alternateContactMobilePhoneNumber)) return true;
        return isValidMobileNumber(value);
      },
    ),

    // ── Home Address ─────────────────────────────────────────────────────
    addressLine1: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.homeAddressLine1),
      then: schema => schema.required(t('contactInformation.errors.addressLine1Required')),
    }),

    addressLine2: Yup.string().nullable().notRequired(),

    city: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.homeAddressCity),
      then: schema => schema.required(t('contactInformation.errors.cityRequired')),
    }),

    zipCode: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.homeAddressZipPostal),
      then: schema => schema.required(t('contactInformation.errors.zipCodeRequired')),
    }),

    country: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.homeAddressCountry),
      then: schema => schema.required(t('contactInformation.errors.countryRequired')),
    }),

    state: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.homeAddressStateProvince),
      then: schema => schema.required(t('contactInformation.errors.stateRequired')),
    }),

    // ── Emergency Contact ────────────────────────────────────────────────
    emergencyContactName: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.emergencyContactName),
      then: schema => schema.required(t('contactInformation.errors.emergencyContactNameRequired')),
    }),

    emergencyContactNumber: Yup.string().test(
      'emergency-phone',
      t('contactInformation.errors.phoneInvalid'),
      function (value) {
        const isMandatory = isFieldRequired(WorkerProfileField.emergencyContactMobilePhone);
        const nationalNumber = extractNationalNumber(value);
        const hasNumber = nationalNumber && nationalNumber.trim() !== '';
        if (isMandatory && !hasNumber) {
          return this.createError({ message: t('contactInformation.errors.emergencyContactNumberRequired') });
        }
        if (hasNumber) return isValidMobileNumber(value);
        return true;
      },
    ),

    emergencyContactRelationship: Yup.string().when([], {
      is: () => isFieldRequired(WorkerProfileField.emergencyContactRelationship),
      then: schema => schema.required(t('contactInformation.errors.emergencyContactRelationshipRequired')),
    }),

    // ── Worker Trades ────────────────────────────────────────────────────
    isSupervisor: isFieldRequired(WorkerProfileField.workerRole)
      ? Yup.boolean().nullable().required(t('trades.errors.roleRequired')).typeError(t('trades.errors.roleRequired'))
      : Yup.boolean().nullable(),

    isAffiliatedToLaborUnion: isFieldRequired(WorkerProfileField.workerLaborUnionStatus)
      ? Yup.boolean()
          .nullable()
          .required(t('trades.errors.laborUnionRequired'))
          .typeError(t('trades.errors.laborUnionRequired'))
      : Yup.boolean().nullable(),

    isSkilled: isFieldRequired(WorkerProfileField.workerWorkExperience)
      ? Yup.boolean()
          .nullable()
          .required(t('trades.errors.workExperienceRequired'))
          .typeError(t('trades.errors.workExperienceRequired'))
      : Yup.boolean().nullable(),

    laborUnionNumber: Yup.string().when('isAffiliatedToLaborUnion', {
      is: (val: boolean) => val === true,
      // Mandatory whenever labor union = YES, as on the Trades step. A blank value used to be blocked
      // only by the min-length rule.
      then: schema =>
        schema
          .required(t('trades.errors.laborUnionNumberRequired'))
          .trim()
          .test(minLengthIfFilled(3, t('trades.errors.laborUnionNumberMin')))
          .max(20, t('trades.errors.laborUnionNumberMax')),
      otherwise: schema => schema.notRequired(),
    }),

    trades: isFieldRequired(WorkerProfileField.workerTrades)
      ? Yup.array()
          .of(Yup.string().required())
          .required(t('trades.errors.selectedTradesRequired'))
          .min(1, t('trades.errors.selectedTradesMinimum'))
      : Yup.array().of(Yup.string()),

    otherTradesList: Yup.array().of(
      Yup.object().shape({
        isSaved: Yup.boolean().required(),
        OtherText: Yup.string().required().max(100, t('trades.errors.otherTradeMax')),
      }),
    ),
  }) as Yup.ObjectSchema<IProfileInformationSchema>;
};

export default profileInformationSchema;
